import React from 'react'
import { RadioButton } from 'examkrackers-components'
import { QuizOption, QuizQuestion } from './quiz.types'
import arrowDown from 'assets/arrowDown.svg'
import arrowUp from 'assets/arrowUp.svg'
import success from 'assets/success.svg'
import fail from 'assets/fail.svg'
import {
  AnswersCard,
  AnswersCardHeader,
  AnswersHeaderCaret,
  AnswersHeaderLabel,
  OptionLabel,
  OptionButton,
  OptionChoiceRow,
  OptionExplanation,
  OptionLetter,
  OptionList,
  OptionRadioWrapper,
  QuestionPrompt,
  RevealedOptionIcon,
  ShowAnswerChoicesButton,
  SuccessAction
} from './QuizModal.styles'
import { getIsIosTouchDevice } from './quizIosTouchDevice'
import QuizDeltaContent from './QuizDeltaContent'
import { QuillDelta, hasQuillDelta } from './quizDelta'

type OptionVisualState = 'default' | 'correct' | 'incorrect' | 'revealed'

interface QuizQuestionViewProps {
  question: QuizQuestion
  selectedOptionId: string | null
  revealedOptionIds: string[]
  isAnswersVisible: boolean
  canToggleAnswersVisible: boolean
  onToggleAnswersVisible: () => void
  answersHeaderLabel: string
  answersHeaderCaret: boolean
  showAnswerChoicesLabel: string
  onShowAnswerChoices: () => void
  onSelectOption: (optionId: string) => void
}

const getOptionVisualState = (
  optionId: string,
  correctOptionId: string,
  selectedOptionId: string | null,
  revealedOptionIds: string[]
): OptionVisualState => {
  const isRevealed = revealedOptionIds.includes(optionId)

  if (!isRevealed) {
    return 'default'
  }

  // The correct choice is always green after an attempt, even if the student picked a different option.
  if (optionId === correctOptionId) {
    return 'correct'
  }

  // Only the student's first selected wrong answer is red.
  if (optionId === selectedOptionId) {
    return 'incorrect'
  }

  // Other choices the student opens later are review-only (gray).
  return 'revealed'
}

const stripExplanationVerdictPrefix = (explanation: string): string =>
  explanation.replace(/^(correct|incorrect)\s*[.;:]\s*/i, '').trim()

const capitalizeFirstLetter = (text: string): string => {
  if (!text) {
    return text
  }

  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`
}

const formatOptionExplanation = (explanation: string): string =>
  capitalizeFirstLetter(stripExplanationVerdictPrefix(explanation))

type OptionExplanationContent = {
  delta: QuillDelta | null
  text: string
}

const getOptionExplanationContent = (
  option: QuizOption,
  question: QuizQuestion
): OptionExplanationContent => {
  if (hasQuillDelta(option.explanation_delta)) {
    return {
      delta: option.explanation_delta as QuillDelta,
      text: option.explanation || ''
    }
  }

  if (option.explanation) {
    return {
      delta: null,
      text: formatOptionExplanation(option.explanation)
    }
  }

  if (option.id === question.correctOptionId) {
    if (hasQuillDelta(question.explanation_delta)) {
      return {
        delta: question.explanation_delta as QuillDelta,
        text: question.explanation || ''
      }
    }

    return {
      delta: null,
      text: formatOptionExplanation(question.explanation)
    }
  }

  return { delta: null, text: '' }
}

const renderOptionStateIcon = (optionState: OptionVisualState) => {
  if (optionState === 'correct') {
    return (
      <img
        src={success}
        alt='success'
        height={15}
        style={{ marginTop: '3px' }}
      />
    )
  }

  if (optionState === 'incorrect') {
    return (
      <img src={fail} alt='fail' height={15} style={{ marginTop: '3px' }} />
    )
  }

  return (
    <RevealedOptionIcon viewBox='0 0 21 21' fill='none' aria-hidden='true'>
      <path
        d='M15.75 5.25L5.25 15.75M5.25 5.25L15.75 15.75'
        stroke='#6C5F68'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </RevealedOptionIcon>
  )
}

const QuizQuestionView: React.FC<QuizQuestionViewProps> = ({
  question,
  selectedOptionId,
  revealedOptionIds,
  isAnswersVisible,
  canToggleAnswersVisible,
  onToggleAnswersVisible,
  answersHeaderLabel,
  answersHeaderCaret,
  showAnswerChoicesLabel,
  onShowAnswerChoices,
  onSelectOption
}) => {
  const optionListContentRef = React.useRef<HTMLDivElement | null>(null)
  const [optionListHeight, setOptionListHeight] = React.useState(0)
  const isIosTouchLayout = React.useMemo(() => getIsIosTouchDevice(), [])
  const revealedOptionKey = revealedOptionIds.join(',')

  React.useLayoutEffect(() => {
    const el = optionListContentRef.current
    if (!el) {
      return
    }

    const measure = () => {
      setOptionListHeight(el.scrollHeight)
    }

    measure()

    if (!isIosTouchLayout) {
      return
    }

    let nestedRaf = 0
    const outerRaf = requestAnimationFrame(() => {
      nestedRaf = requestAnimationFrame(measure)
    })

    return () => {
      cancelAnimationFrame(outerRaf)
      cancelAnimationFrame(nestedRaf)
    }
  }, [
    question.options,
    selectedOptionId,
    revealedOptionKey,
    isAnswersVisible,
    isIosTouchLayout
  ])

  const handleOptionClick =
    (optionId: string, optionState: OptionVisualState) =>
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (optionState !== 'default') {
        return
      }

      const target = event.target as HTMLElement
      if (target.closest("input[type='radio']")) {
        return
      }

      if (target.closest('label')) {
        event.preventDefault()
      }

      onSelectOption(optionId)
    }

  const handleOptionChange = (optionId: string) => () => {
    onSelectOption(optionId)
  }

  return (
    <div>
      <QuestionPrompt>
        <QuizDeltaContent
          delta={question.prompt_delta}
          fallbackText={question.prompt}
        />
      </QuestionPrompt>
      {!isAnswersVisible ? (
        <ShowAnswerChoicesButton
          type='button'
          aria-label={showAnswerChoicesLabel}
          onClick={onShowAnswerChoices}
        >
          {showAnswerChoicesLabel}
        </ShowAnswerChoicesButton>
      ) : (
        <AnswersCard>
          <AnswersCardHeader
            type='button'
            onClick={onToggleAnswersVisible}
            disabled={!canToggleAnswersVisible}
            isVisible={canToggleAnswersVisible}
            isOpen={answersHeaderCaret}
          >
            <AnswersHeaderLabel>{answersHeaderLabel}</AnswersHeaderLabel>
            {canToggleAnswersVisible && (
              <AnswersHeaderCaret>
                {!answersHeaderCaret ? (
                  <img src={arrowDown} alt='arrow down' />
                ) : (
                  <img src={arrowUp} alt='arrow up' />
                )}
              </AnswersHeaderCaret>
            )}
          </AnswersCardHeader>
          <OptionList
            isVisible={isAnswersVisible}
            expandedHeight={optionListHeight}
            $isIosLayoutFix={isIosTouchLayout}
          >
            <div ref={optionListContentRef}>
              {question?.options?.map((option, index) => {
                const optionLetter = String.fromCharCode(65 + index)
                const optionState = getOptionVisualState(
                  option.id,
                  question.correctOptionId,
                  selectedOptionId,
                  revealedOptionIds
                )
                const isSelected = selectedOptionId === option.id
                const explanationContent = getOptionExplanationContent(
                  option,
                  question
                )
                const shouldShowExplanation =
                  optionState !== 'default' &&
                  (hasQuillDelta(explanationContent.delta) ||
                    Boolean(explanationContent.text))
                const optionLabel = (
                  <>
                    <OptionLetter $isIosLayoutFix={isIosTouchLayout}>
                      {optionLetter}.
                    </OptionLetter>
                    <OptionLabel
                      $isIosLayoutFix={isIosTouchLayout}
                      $hasRichContent={hasQuillDelta(option.text_delta)}
                    >
                      <QuizDeltaContent
                        delta={option.text_delta}
                        fallbackText={option.text}
                      />
                    </OptionLabel>
                  </>
                )

                return (
                  <OptionButton
                    key={option.id}
                    state={optionState}
                    onClick={handleOptionClick(option.id, optionState)}
                  >
                    <OptionChoiceRow
                      state={optionState}
                      $isIosLayoutFix={isIosTouchLayout}
                    >
                      <OptionRadioWrapper $isIosLayoutFix={isIosTouchLayout}>
                        {optionState === 'default' ? (
                          <RadioButton
                            alignTop
                            value={option.id}
                            name={`quiz-question-${question.id}`}
                            isChecked={isSelected}
                            isDisabled={false}
                            label={optionLabel}
                            onChange={handleOptionChange(option.id)}
                          />
                        ) : (
                          <SuccessAction $isIosLayoutFix={isIosTouchLayout}>
                            {renderOptionStateIcon(optionState)}
                            {optionLabel}
                          </SuccessAction>
                        )}
                      </OptionRadioWrapper>
                    </OptionChoiceRow>
                    {shouldShowExplanation && (
                      <OptionExplanation aria-live='polite'>
                        <QuizDeltaContent
                          delta={explanationContent.delta}
                          fallbackText={explanationContent.text}
                        />
                      </OptionExplanation>
                    )}
                  </OptionButton>
                )
              })}
            </div>
          </OptionList>
        </AnswersCard>
      )}
    </div>
  )
}

export default QuizQuestionView
